import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPut } from "../../api";
import { Select } from "../../components/Select";
import type { SelectOption } from "../../components/Select";
import { Skeleton } from "../../components/Loaders";
import type { AdminLlmSettings } from "../../types";

/** Match backend `normalize_ollama_base_url`: trim wrappers and slashes. */
function normalizeOllamaUrl(raw: string): string {
  let s = raw.trim();
  const pairs: [string, string][] = [
    ["(", ")"],
    ["[", "]"],
    ["<", ">"],
  ];
  let changed = true;
  while (changed && s.length >= 2) {
    changed = false;
    for (const [a, b] of pairs) {
      if (s.startsWith(a) && s.endsWith(b)) {
        s = s.slice(1, -1).trim();
        changed = true;
        break;
      }
    }
  }
  s = s.replace(/^["']+|["']+$/g, "").trim();
  return s.replace(/\/+$/, "");
}

export function AdminLlm() {
  const [llm, setLlm] = useState<AdminLlmSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [llmUrl, setLlmUrl] = useState("");
  const [llmModel, setLlmModel] = useState("");
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiGet<AdminLlmSettings>("/api/admin/llm-settings");
      setLlm(data);
      setLlmUrl(data.ollama_url);
      setLlmModel(data.ollama_model);
      setModelOptions(data.installed_models ?? []);
    } catch (e) {
      setError((e as Error).message);
      setLlm(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const modelSelectOptions: SelectOption[] = useMemo(() => {
    const cur = llmModel.trim();
    const seen = new Set<string>();
    const out: SelectOption[] = [];
    if (cur && !modelOptions.includes(cur)) {
      out.push({
        value: cur,
        label: `${cur} — current (not in last fetch)`,
      });
      seen.add(cur);
    }
    for (const m of modelOptions) {
      if (!seen.has(m)) {
        out.push({ value: m, label: m });
        seen.add(m);
      }
    }
    return out;
  }, [modelOptions, llmModel]);

  const modelSelectDisabled =
    modelsLoading || modelSelectOptions.length === 0;

  const fetchModelsForUrl = async () => {
    setError(null);
    setModelsLoading(true);
    try {
      const url = normalizeOllamaUrl(llmUrl);
      setLlmUrl(url);
      const q = new URLSearchParams({ base_url: url });
      const r = await apiGet<{ reachable: boolean; models: string[]; error?: string }>(
        `/api/admin/ollama-models?${q}`,
      );
      setModelOptions(r.models ?? []);
      if (!r.reachable) {
        setError(r.error?.trim() || "Could not reach Ollama at this URL.");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setModelsLoading(false);
    }
  };

  const saveLlmSettings = async () => {
    setError(null);
    setSavingLlm(true);
    try {
      const url = normalizeOllamaUrl(llmUrl);
      setLlmUrl(url);
      const data = await apiPut<AdminLlmSettings>("/api/admin/llm-settings", {
        ollama_url: url,
        ollama_model: llmModel.trim(),
      });
      setLlm(data);
      setLlmUrl(data.ollama_url);
      setLlmModel(data.ollama_model);
      setModelOptions(data.installed_models ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingLlm(false);
    }
  };

  if (loading) {
    return (
      <>
        <h2>LLM connection</h2>
        <Skeleton variant="block" height={220} />
        <Skeleton variant="text" width="55%" />
      </>
    );
  }

  return (
    <>
      <h2>LLM connection</h2>
      <p className="muted admin-llm-page-lead">
        Deck build, analyze, and all server-side Ollama calls use the URL and model saved here
        (written to <code>data/llm_settings.json</code>). If that file is missing, the server
        falls back to <code>OLLAMA_URL</code> and <code>OLLAMA_MODEL</code> in the process
        environment. Point the app at your Ollama host and model, then use <strong>Fetch models</strong> after
        changing the URL to refresh the list from <code>/api/tags</code>.
      </p>

      <section className="admin-llm-panel" aria-label="Ollama URL and model">
        {llm && (
          <p className="admin-llm-env muted">
            Environment defaults:{" "}
            <span className="mono">
              {llm.env_defaults.ollama_url} · {llm.env_defaults.ollama_model}
            </span>
            {llm.settings_persisted ? (
              <span className="admin-llm-pill">Using saved settings</span>
            ) : (
              <span className="admin-llm-pill admin-llm-pill--soft">Using env defaults</span>
            )}
          </p>
        )}
        {error && <div className="error-banner">{error}</div>}
        <div className="admin-llm-grid">
          <label className="field">
            <span>Ollama base URL</span>
            <input
              type="url"
              autoComplete="off"
              placeholder="http://127.0.0.1:11434"
              value={llmUrl}
              onChange={(e) => setLlmUrl(e.target.value)}
            />
            <span className="admin-llm-field-hint muted">
              Paste only the base URL (no parentheses). For Ollama on another machine, run it with{" "}
              <code>OLLAMA_HOST=0.0.0.0</code> so it accepts remote connections, and open port{" "}
              <code>11434</code> in the host firewall.
            </span>
          </label>
          <label className="field">
            <span>Model</span>
            <Select
              id="admin-llm-model"
              ariaLabel="Ollama model"
              value={llmModel}
              onChange={setLlmModel}
              options={modelSelectOptions}
              placeholder={
                modelsLoading
                  ? "Loading models…"
                  : modelSelectOptions.length
                    ? "Select a model"
                    : "Fetch models first"
              }
              disabled={modelSelectDisabled}
              minWidth={280}
            />
          </label>
        </div>
        <div className="admin-llm-actions">
          <button
            type="button"
            className="secondary"
            onClick={fetchModelsForUrl}
            disabled={modelsLoading || !llmUrl.trim()}
          >
            {modelsLoading ? "Fetching…" : "Fetch models"}
          </button>
          <button type="button" onClick={saveLlmSettings} disabled={savingLlm || !llmUrl.trim()}>
            {savingLlm ? "Saving…" : "Save LLM settings"}
          </button>
        </div>
      </section>
    </>
  );
}
